import {
  AttachmentState,
  type AttachmentErrorHandler,
  type LocalStorageAdapter,
} from "@powersync/web"
import { supabase } from "@fieldbook/common/supabase"
import { powerSyncDb } from "./db"
import {
  AUTOMATIC_RETRY_MARKER_PREFIX,
  FAILED_PHOTO_CONDITION,
  FAILED_PHOTO_RETRY_DELAY_MS,
} from "./attachmentFailureQueries"
import { UploadSafeAttachmentQueue } from "./uploadSafeAttachmentQueue"
import { createTransferBackoff, TransferBackoffSkip } from "./transferBackoff"
import {
  projectPhotoCondition,
  projectPhotoParameters,
} from "./projectPhotoQuery"
import { uploadFile } from "@/lib/storageUpload"
import { getActiveProject } from "@/hooks/useActiveProject"
import { getLiveSessionWithTimeout } from "@/lib/auth/liveSession"
import { createAttachmentStorage } from "@/platform"
import type { Session } from "@supabase/supabase-js"
import { MAX_PHOTO_FILE_SIZE_BYTES } from "@fieldbook/common/lib/photoLimits"
import { bucketForPhotoPath } from "@fieldbook/common/lib/photoBuckets"
import {
  AttachmentFileTooLargeError,
  AttachmentPhotoDeletedError,
  AttachmentPhotoNotYetSyncedError,
  getUploadErrorMessage,
  getUploadErrorStatus,
  isPermanentAttachmentUploadError,
} from "./attachmentErrors"
import {
  reportPhotoUploadFailed,
  reportPhotoUploadRefused,
  reportPhotoUploaded,
} from "./uploadSignals"

// Files in app-private storage on native, IndexedDB on the PWA. Native builds
// start on an empty queue at the SQLite cutoff, so nothing is migrated from
// the old IndexedDB store (ADR 0001).
const localStorage = createAttachmentStorage("fieldbook-photos")

// Set while "Reset local data" clears the synced tables. The attachments table
// is local-only and survives the reset, but the photos rows it depends on are
// gone until they re-download, so a missing row stops meaning "deleted".
let localDataResetInProgress = false

export function setLocalDataResetInProgress(inProgress: boolean) {
  localDataResetInProgress = inProgress
}

export function isLocalDataResetInProgress() {
  return localDataResetInProgress
}

/**
 * Whether a missing photos row means the photo was deleted. Before the first
 * full sync after a reset the table is simply incomplete, and treating a
 * pending upload as deleted would archive it and evict the only copy of the
 * bytes.
 */
export function isPhotoTableAuthoritative() {
  return (
    !localDataResetInProgress && Boolean(powerSyncDb.currentStatus?.hasSynced)
  )
}

// A parked upload remains QUEUED_UPLOAD so project changes cannot archive it.
// The durable failure record gates network attempts until explicit retry.
class ParkedAttachmentUpload extends Error {}

export type PermanentAttachmentUploadFailure = {
  attachmentId: string
  status: number | null
  /** The daily automatic retry failed again; the user was already told. */
  automatic: boolean
}

const permanentUploadFailureListeners = new Set<
  (failure: PermanentAttachmentUploadFailure) => void
>()

export function subscribeToPermanentAttachmentUploadFailures(
  listener: (failure: PermanentAttachmentUploadFailure) => void,
): () => void {
  permanentUploadFailureListeners.add(listener)
  return () => permanentUploadFailureListeners.delete(listener)
}

// Without this, a photo whose object is permanently missing (e.g. a failed
// upload from an earlier session) is re-downloaded on every sync pass.
const downloadBackoff = createTransferBackoff()

// Duck-type the Supabase StorageApiError without importing storage-js
// directly (it's not a top-level dep of this app, only a transitive one).
const isObjectNotFound = (err: unknown): boolean => {
  if (!err || typeof err !== "object") return false
  const e = err as { statusCode?: unknown; message?: unknown }
  return e.statusCode === "404" || e.message === "Object not found"
}

/**
 * Resolve the live session for a Storage request, or throw so the attachment
 * queue keeps the item and retries it once auth recovers.
 */
async function requireLiveSession(
  action: "upload" | "download",
): Promise<Session> {
  const session = await getLiveSessionWithTimeout()
  if (!session) throw new Error(`No session — cannot ${action} photo file`)
  return session
}

type ParkedFailure = { id: string; failed_at: string }

/**
 * Gate an upload attempt on its durable failure. Throws to skip the attempt
 * while the failure is parked; clears the failure when the daily automatic
 * retry is due. Never touches the photo's bytes.
 */
async function checkParkedUpload(failure: ParkedFailure | null) {
  if (!failure) return
  const photo = await powerSyncDb.getOptional(
    "SELECT id FROM photos WHERE id = ?",
    [failure.id],
  )
  if (!photo) {
    if (!isPhotoTableAuthoritative()) throw new ParkedAttachmentUpload()
    await powerSyncDb.execute("DELETE FROM attachment_failures WHERE id = ?", [
      failure.id,
    ])
    throw new AttachmentPhotoDeletedError(failure.id)
  }
  const failedAt = Date.parse(failure.failed_at)
  if (
    Number.isFinite(failedAt) &&
    Date.now() - failedAt < FAILED_PHOTO_RETRY_DELAY_MS
  ) {
    throw new ParkedAttachmentUpload()
  }
  // One automatic attempt per day, persisted across restarts. A new permanent
  // rejection records a fresh failed_at; transient errors use normal retries.
  // The marker lets that rejection skip the toast, so users aren't told once
  // per parked photo every day, even if transient errors or a restart come
  // first.
  await powerSyncDb.writeTransaction(async (tx) => {
    await tx.execute("DELETE FROM attachment_failures WHERE id = ?", [
      failure.id,
    ])
    await tx.execute(
      `INSERT OR REPLACE INTO attachment_queue_meta (id, value) VALUES (?, ?)`,
      [AUTOMATIC_RETRY_MARKER_PREFIX + failure.id, new Date().toISOString()],
    )
  })
}

// The SDK reads a file's full bytes before calling remoteStorage.uploadFile, so
// gate parked failures here to avoid reading every parked photo from IndexedDB
// on each sync pass. Only queue uploads use this adapter's readFile;
// display/export reads use the original adapter (readAttachmentFile) so failed
// photos stay viewable without retrying them.
const uploadStorage: LocalStorageAdapter = {
  initialize: () => localStorage.initialize(),
  getLocalUri: (name) => localStorage.getLocalUri(name),
  saveFile: (path, data) => localStorage.saveFile(path, data),
  deleteFile: (path) => localStorage.deleteFile(path),
  fileExists: (path) => localStorage.fileExists(path),
  makeDir: (path) => localStorage.makeDir(path),
  rmDir: (path) => localStorage.rmDir(path),
  clear: () => localStorage.clear(),
  async readFile(path) {
    await checkParkedUpload(
      await powerSyncDb.getOptional<ParkedFailure>(
        `SELECT f.id, f.failed_at FROM attachment_failures f
       JOIN attachments a ON a.id = f.id WHERE a.local_uri = ? AND a.state = ?`,
        [path, AttachmentState.QUEUED_UPLOAD],
      ),
    )
    return localStorage.readFile(path)
  },
}

const remoteStorage = {
  async uploadFile(
    fileData: ArrayBuffer,
    attachment: { filename: string; mediaType?: string; id: string },
  ) {
    await checkParkedUpload(
      await powerSyncDb.getOptional<ParkedFailure>(
        "SELECT id, failed_at FROM attachment_failures WHERE id = ?",
        [attachment.id],
      ),
    )

    if (fileData.byteLength > MAX_PHOTO_FILE_SIZE_BYTES) {
      throw new AttachmentFileTooLargeError(
        fileData.byteLength,
        MAX_PHOTO_FILE_SIZE_BYTES,
      )
    }

    const session = await requireLiveSession("upload")

    // Look up the photo record in PowerSync to get the file_path
    const row = await powerSyncDb.getOptional<{
      file_path: string
      mime_type: string
    }>("SELECT file_path, mime_type FROM photos WHERE id = ?", [attachment.id])
    if (!row?.file_path) {
      if (!isPhotoTableAuthoritative()) {
        throw new AttachmentPhotoNotYetSyncedError(attachment.id)
      }
      throw new AttachmentPhotoDeletedError(attachment.id)
    }

    const blob = new Blob([fileData], {
      type: attachment.mediaType ?? "application/octet-stream",
    })

    const bucket = bucketForPhotoPath(row.file_path)
    await uploadFile(
      bucket,
      row.file_path,
      blob,
      { contentType: row.mime_type ?? "image/jpeg" },
      session,
    )

    // A delete during the upload removes the storage object before this one
    // lands, which would leave it orphaned. Clean up after it instead. Best
    // effort: a retry would find the row gone and never reach this point.
    const stillExists = await powerSyncDb.getOptional(
      "SELECT id FROM photos WHERE id = ?",
      [attachment.id],
    )
    if (!stillExists) {
      // A reset that lands mid-upload empties the table too; keep the object
      // and let the retry after the reset find the row again.
      if (!isPhotoTableAuthoritative()) {
        throw new AttachmentPhotoNotYetSyncedError(attachment.id)
      }
      const { error } = await supabase.storage
        .from(bucket)
        .remove([row.file_path])
      if (error) {
        console.warn(
          `[Attachments] Couldn't remove upload for deleted photo ${attachment.id}:`,
          error.message,
        )
      }
      throw new AttachmentPhotoDeletedError(attachment.id)
    }
    reportPhotoUploaded()
  },

  async downloadFile(attachment: {
    id: string
    mediaType?: string
  }): Promise<ArrayBuffer> {
    if (downloadBackoff.isBackingOff(attachment.id)) {
      throw new TransferBackoffSkip(attachment.id)
    }

    await requireLiveSession("download")

    // Look up the file_path from the photo record
    const row = await powerSyncDb.getOptional<{ file_path: string }>(
      "SELECT file_path FROM photos WHERE id = ?",
      [attachment.id],
    )
    if (!row?.file_path) {
      throw new Error(`Photo record not found for attachment ${attachment.id}`)
    }

    const { data, error } = await supabase.storage
      .from(bucketForPhotoPath(row.file_path))
      .download(row.file_path)
    if (error) {
      throw error
    }
    downloadBackoff.clear(attachment.id)
    return data.arrayBuffer()
  },

  async deleteFile(attachment: { id: string }) {
    const row = await powerSyncDb.getOptional<{ file_path: string }>(
      "SELECT file_path FROM photos WHERE id = ?",
      [attachment.id],
    )
    if (!row?.file_path) return

    const { error } = await supabase.storage
      .from(bucketForPhotoPath(row.file_path))
      .remove([row.file_path])
    if (error) throw error
  },
}

/** Whether the photo row is gone and that really means it was deleted. */
async function isDeletedPhoto(photoId: string): Promise<boolean> {
  if (!isPhotoTableAuthoritative()) return false
  try {
    const row = await powerSyncDb.getOptional(
      "SELECT id FROM photos WHERE id = ?",
      [photoId],
    )
    return !row
  } catch {
    return false
  }
}

const errorHandler: AttachmentErrorHandler = {
  async onDownloadError(attachment, error) {
    // Sentinel from our own backoff gate — keep the attachment queued so
    // we'll try again after the window expires, but don't log anything.
    if (error instanceof TransferBackoffSkip) return true

    if (isObjectNotFound(error)) {
      const { failures, delayMs } = downloadBackoff.recordFailure(attachment.id)
      // Log once per real failure (not per skipped tick) so the issue is
      // still visible without flooding the console.
      console.warn(
        `[Attachments] Photo object missing for ${attachment.id} (attempt ${failures}); next retry in ${Math.round(delayMs / 60000)}m`,
      )
      return true
    }

    console.error(`[Attachments] Download failed for ${attachment.id}:`, error)
    return true
  },
  async onUploadError(attachment, error) {
    if (error instanceof ParkedAttachmentUpload) return true

    // The queue only archives unsynced uploads that we decline to retry, so
    // without this a photo deleted mid-upload would fail on every sync tick.
    if (error instanceof AttachmentPhotoDeletedError) {
      console.info(
        `[Attachments] Dropping upload for deleted photo ${attachment.id}`,
      )
      return false
    }

    if (error instanceof AttachmentPhotoNotYetSyncedError) {
      console.info(
        `[Attachments] Deferring upload for ${attachment.id} until its photo record syncs`,
      )
      return true
    }

    // A photo deleted while a sync pass already held it, e.g. by deleting its
    // observation, which also removes the bytes. Reading the file then fails
    // before uploadFile can notice the missing row, and retrying would bring
    // back a QUEUED_UPLOAD row for a file that's gone, failing forever.
    if (await isDeletedPhoto(attachment.id)) {
      console.info(
        `[Attachments] Dropping upload for deleted photo ${attachment.id}`,
      )
      return false
    }

    const status = getUploadErrorStatus(error)
    const message = getUploadErrorMessage(error).slice(0, 1000)
    const permanent = isPermanentAttachmentUploadError(error)

    // Do not log the raw TUS error: originalRequest contains request headers,
    // including the Supabase bearer token. Keep diagnostics explicit and safe.
    console.error("[Attachments] Upload failed", {
      attachmentId: attachment.id,
      size: attachment.size ?? null,
      status,
      message,
      retryable: !permanent,
    })

    if (!permanent) reportPhotoUploadFailed()

    if (permanent) {
      reportPhotoUploadRefused()
      const automatic = await powerSyncDb.writeTransaction(async (tx) => {
        await tx.execute(
          `INSERT OR REPLACE INTO attachment_failures (id, status, message, failed_at)
           VALUES (?, ?, ?, ?)`,
          [attachment.id, status, message, new Date().toISOString()],
        )
        const marker = AUTOMATIC_RETRY_MARKER_PREFIX + attachment.id
        const marked = await tx.getOptional(
          "SELECT id FROM attachment_queue_meta WHERE id = ?",
          [marker],
        )
        await tx.execute("DELETE FROM attachment_queue_meta WHERE id = ?", [
          marker,
        ])
        return Boolean(marked)
      })
      for (const listener of permanentUploadFailureListeners) {
        try {
          listener({ attachmentId: attachment.id, status, automatic })
        } catch (listenerError) {
          console.error(
            "[Attachments] Permanent upload failure listener failed:",
            listenerError,
          )
        }
      }
      // Returning false lets the SDK archive and eventually delete the only
      // local copy. Keep it queued; remoteStorage gates subsequent attempts.
      return true
    }

    return true
  },
  async onDeleteError(_attachment, error) {
    console.error("[Attachments] Delete failed:", error)
    return true
  },
}

export const attachmentQueue = new UploadSafeAttachmentQueue({
  db: powerSyncDb,
  localStorage: uploadStorage,
  remoteStorage,
  errorHandler,
  // Low throttle so a just-submitted observation's photos start uploading
  // near-immediately rather than waiting up to 20s for the next sync pass.
  // The queue still coalesces multiple sync triggers within this window.
  syncThrottleDuration: 1000,
  watchAttachments: (onUpdate, signal) => {
    const projectId = getActiveProject()

    // Only watch photos belonging to the active project.
    const query = projectId
      ? `SELECT DISTINCT p.id, p.filename FROM photos p
         WHERE p.filename IS NOT NULL
         AND ${projectPhotoCondition("p")}`
      : "SELECT id, filename FROM photos WHERE 0"

    const parameters = projectId ? projectPhotoParameters(projectId) : []

    powerSyncDb.watch(
      query,
      parameters,
      {
        onResult: async (result) => {
          const attachments = (result.rows?._array ?? []).map(
            (row: { id: string; filename: string }) => ({
              id: row.id,
              fileExtension: row.filename.split(".").pop() ?? "jpg",
            }),
          )
          await onUpdate(attachments)
        },
      },
      { signal },
    )
  },
  downloadAttachments: true,
  archivedCacheLimit: 500,
})

/**
 * Durable failures for #209's upload detail sheet. The id is the photo and
 * attachment id; join to the photo association tables to resolve its project.
 */
export type AttachmentFailureRow = {
  id: string
  status: number | null
  message: string
  failed_at: string
}

export async function getPermanentAttachmentUploadFailures(): Promise<
  AttachmentFailureRow[]
> {
  return powerSyncDb.getAll<AttachmentFailureRow>(
    `SELECT f.* FROM attachment_failures f
     JOIN attachments a ON a.id = f.id
     WHERE ${FAILED_PHOTO_CONDITION}
     ORDER BY f.failed_at DESC`,
  )
}

/** Matches PR #282's retry API; also recovers legacy archived failures. */
export async function retryPermanentlyFailedPhotoUpload(
  photoId: string,
): Promise<void> {
  await powerSyncDb.writeTransaction(async (tx) => {
    await tx.execute(
      `UPDATE attachments SET state = ?
       WHERE id = ? AND state IN (?, ?) AND has_synced = 0
       AND local_uri IS NOT NULL AND local_uri != ''`,
      [
        AttachmentState.QUEUED_UPLOAD,
        photoId,
        AttachmentState.QUEUED_UPLOAD,
        AttachmentState.ARCHIVED,
      ],
    )
    await tx.execute("DELETE FROM attachment_failures WHERE id = ?", [photoId])
    // The user asked for this attempt, so a repeat failure should tell them.
    await tx.execute("DELETE FROM attachment_queue_meta WHERE id = ?", [
      AUTOMATIC_RETRY_MARKER_PREFIX + photoId,
    ])
  })
  // A parked row stays QUEUED_UPLOAD, so the SDK differential watch may see
  // no change. Nudge the queue immediately, handling background errors.
  void attachmentQueue.syncStorage().catch((error) => {
    console.error("[Attachments] Retry sync failed:", error)
  })
}

/**
 * Read a photo file from local attachment storage.
 * Returns an ArrayBuffer, or null if not available locally.
 */
export async function readAttachmentFile(
  photoId: string,
): Promise<ArrayBuffer | null> {
  try {
    const row = await powerSyncDb.getOptional<{
      local_uri: string
      state: number
    }>("SELECT local_uri, state FROM attachments WHERE id = ?", [photoId])
    if (!row?.local_uri) return null
    return await localStorage.readFile(row.local_uri)
  } catch (error) {
    console.error(
      `[Attachments] Failed to read file for photo ${photoId}:`,
      error,
    )
    return null
  }
}
